import { ANEX, AnexApiClient } from '@anex/common'
import { makeAutoObservable } from 'mobx'

import { RootStore } from './RootStore'
import { TokenManager } from '~/utils/TokenManager'
import { IProfile, IRadius, IService, IVoIPService } from '~/types'

/**
 * API Calls or other data here should be related to the customer account
 * ie: services, radius status of service, profile editing, adding contacts
 */
export class AccountStore {
  api: AnexApiClient
  services: IService[] = []
  currentService: IService = {} as IService
  profile: IProfile = {} as IProfile
  radius: Partial<IRadius> = {
    timeOnline: '',
    isConnected: false,
    framedIpAddress: '',
  }

  voip: Partial<IVoIPService> = null

  constructor(rootStore: RootStore) {
    this.api = rootStore.api

    makeAutoObservable(this)
  }

  get getCurrentService() {
    return this.currentService
  }

  get getServices() {
    return this.services
  }

  get getVoip() {
    return this.voip
  }

  get getProfile() {
    return this.profile
  }

  get getRadius() {
    return this.radius
  }

  /**
   * Set Profile
   * @param profile
   */
  *setProfile(profile: IProfile) {
    this.profile = yield profile
  }

  /**
   * Set selected services from the dropdown in the navbar
   * Here we uses flow (https://mobx.js.org/actions.html#using-flow-instead-of-async--await-)
   * to be sure we set the current service properly before continuing
   * @param index To get the service in the array
   */
  *setSelectedService(index: number): any {
    this.currentService = yield this.services[index]
    if (
      !this.services[index].notes ||
      this.services[index].notes.length === 0
    ) {
      yield this.getServiceNotes(this.services[index].id)
    }
  }

  *setVoipServiceDetails(voip: IVoIPService) {
    this.voip = yield voip
  }

  /**
   * Set Current Service Notes
   */
  *setCurrentServiceNotes(
    notes: ANEX.Website.API.Endpoints.V1.Account.Services.Notes.Note[]
  ) {
    if (this.currentService) {
      this.currentService.notes = yield notes
    }
  }

  /**
   * Radius set
   */
  *setRadius(radius: Partial<IRadius>) {
    this.radius = yield radius
  }

  /**
   * Get the notes for the current service
   * This will be called when the current service is changed
   * @param serviceId
   * @returns
   */
  async getServiceNotes(serviceId: string | undefined) {
    return await this.api.V1.Account.Services.Notes(
      serviceId || '',
      TokenManager.getTokenHeader()
    )
      .then((resp) => this.setCurrentServiceNotes(resp.notes || []))
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw new Error(reason.message)
      })
  }

  /**
   * Load Account Profile ie: Details and Contacts
   * Should be called right after logging in or on page reload if user is already logged in
   */
  async loadProfile(): Promise<void> {
    // Chaining both for getting User Details and Contacts
    await this.api.V1.Account.Contacts.UserDetails(
      TokenManager.getTokenHeader()
    ).then(
      async (
        resp: ANEX.Website.API.Endpoints.V1.Account.Contacts.UserDetails.UserDetailsResponse
      ) => {
        this.setProfile({ ...resp } as IProfile)

        await this.api.V1.Account.Contacts.Index(
          '',
          TokenManager.getTokenHeader()
        )
          .then(
            (
              contacts: ANEX.Website.API.Endpoints.V1.Account.Contacts.ContactsResponse
            ) => {
              if (this.profile) {
                this.profile.preferredMessageType =
                  contacts.preferredMessageType || 'Email'
                  this.profile.contactMethods = contacts.contactMethods || []
                  this.profile.contacts = contacts.contacts || []          
              }
            }
          )
          .catch((reason: any) => {
            // we can implement an error logging if needed
            if (import.meta.env.MODE === 'development') {
              console.info(reason)
            }
          })
      }
    )
  }

  /**
   * Load all services under the account
   */
  async loadServices(): Promise<void> {
    return this.api.V2.Account.Services.Index(
      '',
      [],
      TokenManager.getTokenHeader()
    )
      .then(
        (
          resp: ANEX.Website.API.Endpoints.V1.Account.Services.ServicesResponse
        ) => {
          // Sort the services by Service Code then by Service Status
          const servicesItems: IService[] = resp.services
            ? resp.services
                .sort(
                  (a: any) =>
                    parseInt((a.code as string).substring(1)) -
                    parseInt((a.code as string).substring(1))
                )
                .sort((a: any, b: any) =>
                  (b.type as string).localeCompare(a.type)
                )
                .sort((a: any) => (a.serviceStatus !== 'Cancelled' ? -1 : 1))
            : []

          this.services = servicesItems
          this.setSelectedService(0)
        }
      )
      .catch((reason: any) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
      })
  }

  /**
   * Check Radius or VoIP Service Detail
   * Should be called when current service has been changed
   */
  async checkRadiusStatus() {
    const serviceID = this.currentService?.id || ''

    const avcID = this.currentService?.avcId || ''
    await this.api.V1.Account.Services.IsInRadius(
      serviceID,
      avcID,
      TokenManager.getTokenHeader()
    )
      .then((resp) => {
        const radiusResponse = {
          timeOnline: resp.timeOnline || '',
          isConnected: resp.isConnected || false,
          framedIpAddress: resp.framedIpAddress || '',
        }
        this.setRadius(radiusResponse)
      })
      .catch((reason: any) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Create Support Ticket
   */
  async createSupportTicket(payload: FormData) {
    return await this.api.V1.Account.Actions.SupportTicket(
      payload,
      TokenManager.getTokenHeader()
    )
      .then((resp) => {
        if (resp) {
          return resp
        }
      })
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Update Profile
   */
  async postUpdateProfile(
    payload: ANEX.Website.API.Endpoints.V1.Account.Contacts.Update.UpdateRequest
  ) {
    return await this.api.V1.Account.Contacts.Update(
      payload,
      TokenManager.getTokenHeader()
    )
      .then(() => {
        this.loadProfile()
        return true
      })
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.response.data
      })
  }

  /**
   * Get the VoIP Service Settings
   */
  async getVoipService(anexServiceId: string) {
    return this.api.V1.Superloop.ServiceManagement.VoipServices(
      anexServiceId,
      TokenManager.getTokenHeader()
    )
      .then((resp) => {
        this.setVoipServiceDetails(resp)
        return resp
      })
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Update VoIP Service
   * @param anexServiceId
   * @returns
   */
  async updateVoipService(
    anexServiceId: string,
    payload: ANEX.Website.API.Endpoints.V1.Superloop.VoipService.VoipServiceRequest
  ) {
    return this.api.V1.Superloop.ServiceManagement.UpdateVoipServie(
      anexServiceId,
      payload,
      TokenManager.getTokenHeader()
    )
      .then((resp) => {
        console.log(resp)
        this.setVoipServiceDetails(resp)
        return resp
      })
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }
}
